import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { useAuth, hasRole } from '../contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PageHeader } from '../components/PageHeader';
import { StatusBadge } from '../components/StatusBadge';
import { STATUS_LABELS, formatDate } from '../lib/format';
import { Plus, Search, Edit2, Trash2, X, RefreshCw, Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle, MapPin, Camera } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { EquipmentLocationPhotos } from '../components/EquipmentLocationPhotos';
import { PhotoLightbox } from '../components/PhotoLightbox';

const STATUSES = ['operational','breakdown','idle','inefficient','scrap','for_disposal','maintenance','reserved'];

const initialForm = {
  equipment_code: '', category: '', description: '', make: '', model: '', year: '', plate_no: '', engine_no: '', chassis_no: '',
  company_assigned: '', area: '', project_location: '', status: 'idle', acquisition_date: '', cr_expiration: '', or_expiration: '', tp_expiration: '', capacity: '', remarks: '',
};

const Equipment = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || '';
  const [vehicles, setVehicles] = useState([]);
  const [dropdowns, setDropdowns] = useState({ company_assigned: [], area: [], project_location: [], equipment_category: [] });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [companyFilter, setCompanyFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  // Upload state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // Location & Photos dialog
  const [lpOpen, setLpOpen] = useState(false);
  const [lpVehicle, setLpVehicle] = useState(null);

  // Photo lightbox (read-only viewer)
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxVehicle, setLightboxVehicle] = useState(null);

  const canEdit = hasRole(user, 'super_admin','admin','supervisor');
  const canDelete = hasRole(user, 'super_admin','admin');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/vehicles', { params: { search: search || undefined, status_filter: statusFilter || undefined, company: companyFilter || undefined, area: areaFilter || undefined } });
      setVehicles(r.data);
    } finally { setLoading(false); }
  }, [search, statusFilter, companyFilter, areaFilter]);

  const loadDropdowns = useCallback(async () => {
    const r = await api.get('/dropdowns');
    const grouped = {};
    for (const d of r.data) {
      grouped[d.category] = grouped[d.category] || [];
      grouped[d.category].push(d.value);
    }
    setDropdowns({ ...dropdowns, ...grouped });
    // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDropdowns(); }, [loadDropdowns]);
  useEffect(() => {
    if (searchParams.get('new') === '1' && canEdit) { openCreate(); searchParams.delete('new'); setSearchParams(searchParams); }
    // eslint-disable-next-line
  }, [searchParams]);

  const openCreate = () => { setEditing(null); setForm(initialForm); setOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({ ...initialForm, ...v, year: v.year || '' });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, year: form.year ? Number(form.year) : null };
      if (editing) {
        await api.put(`/vehicles/${editing.id}`, payload);
        toast({ title: 'Equipment updated' });
      } else {
        await api.post('/vehicles', payload);
        toast({ title: 'Equipment created' });
      }
      setOpen(false); load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete ${v.equipment_code}?`)) return;
    try {
      await api.delete(`/vehicles/${v.id}`);
      toast({ title: 'Deleted' });
      load();
    } catch (e) {
      toast({ title: 'Cannot delete', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const batchDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} item(s)?`)) return;
    try {
      const r = await api.post('/vehicles/batch-delete', { ids: Array.from(selected) });
      toast({ title: 'Batch delete', description: `Deleted: ${r.data.deleted.length}, Blocked: ${r.data.blocked.length}` });
      setSelected(new Set());
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const onUploadFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const name = f.name.toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.csv')) {
      toast({ title: 'Invalid file', description: 'Only .xlsx or .csv files are accepted', variant: 'destructive' });
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Maximum 10MB', variant: 'destructive' });
      return;
    }
    setUploadFile(f);
    setUploadResult(null);
  };

  const submitUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      const r = await api.post('/vehicles-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadResult(r.data);
      const created = r.data.created?.length || 0;
      const skipped = r.data.skipped?.length || 0;
      const errors = r.data.errors?.length || 0;
      if (created > 0) {
        toast({ title: 'Upload complete', description: `${created} added, ${skipped} skipped, ${errors} errors` });
      } else if (errors === 0 && skipped > 0) {
        toast({ title: 'No new equipment added', description: `${skipped} skipped (duplicates)`, variant: 'destructive' });
      } else {
        toast({ title: 'Upload finished with issues', description: `${errors} errors, ${skipped} skipped`, variant: 'destructive' });
      }
      load();
    } catch (e) {
      toast({ title: 'Upload failed', description: e.response?.data?.detail || 'Server error', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get('/vehicles-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'equipment_inventory_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Failed to download template', variant: 'destructive' });
    }
  };

  const resetUpload = () => {
    setUploadFile(null);
    setUploadResult(null);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Equipment Management"
        description="Manage your fleet inventory, status, and assignments."
        actions={
          <>
            {selected.size > 0 && canDelete && (
              <Button variant="destructive" onClick={batchDelete}><Trash2 className="h-4 w-4 mr-1.5" /> Delete ({selected.size})</Button>
            )}
            <Button variant="outline" onClick={load} disabled={loading}><RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
            {canEdit && <Button variant="outline" onClick={() => { resetUpload(); setUploadOpen(true); }}><Upload className="h-4 w-4 mr-1.5" /> Upload</Button>}
            {canEdit && <Button onClick={openCreate} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]"><Plus className="h-4 w-4 mr-1.5" /> Add Equipment</Button>}
          </>
        }
      />

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9" placeholder="Search code, plate, make, area..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="All Statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={companyFilter || 'all'} onValueChange={(v) => setCompanyFilter(v === 'all' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="All Companies" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {(dropdowns.company_assigned || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={areaFilter || 'all'} onValueChange={(v) => setAreaFilter(v === 'all' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="All Areas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Areas</SelectItem>
                {(dropdowns.area || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {(search || statusFilter || companyFilter || areaFilter) && (
            <div className="mt-3 flex items-center gap-2 text-xs">
              <span className="text-slate-500">Filters:</span>
              {statusFilter && <span className="inline-flex items-center bg-slate-100 px-2 py-1 rounded">Status: {STATUS_LABELS[statusFilter]} <button className="ml-1" onClick={() => setStatusFilter('')}><X className="h-3 w-3" /></button></span>}
              {companyFilter && <span className="inline-flex items-center bg-slate-100 px-2 py-1 rounded">Company: {companyFilter} <button className="ml-1" onClick={() => setCompanyFilter('')}><X className="h-3 w-3" /></button></span>}
              {areaFilter && <span className="inline-flex items-center bg-slate-100 px-2 py-1 rounded">Area: {areaFilter} <button className="ml-1" onClick={() => setAreaFilter('')}><X className="h-3 w-3" /></button></span>}
              <button onClick={() => { setSearch(''); setStatusFilter(''); setCompanyFilter(''); setAreaFilter(''); }} className="text-[hsl(217,91%,24%)] hover:underline">Clear all</button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b text-left text-slate-600">
              <tr>
                {canDelete && <th className="p-3 w-10"><Checkbox checked={selected.size > 0 && selected.size === vehicles.length} onCheckedChange={(c) => setSelected(c ? new Set(vehicles.map((v) => v.id)) : new Set())} /></th>}
                <th className="p-3">Equipment Code</th>
                <th className="p-3">Category</th>
                <th className="p-3">Description</th>
                <th className="p-3">Plate No</th>
                <th className="p-3">Capacity</th>
                <th className="p-3">Company</th>
                <th className="p-3">Area</th>
                <th className="p-3">Location</th>
                <th className="p-3">Status</th>
                <th className="p-3">CR Exp</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {vehicles.length === 0 ? (
                <tr><td colSpan={12} className="p-8 text-center text-slate-500">No equipment found.</td></tr>
              ) : vehicles.map((v) => (
                <tr key={v.id} className="table-row-hover">
                  {canDelete && <td className="p-3"><Checkbox checked={selected.has(v.id)} onCheckedChange={() => toggleSelect(v.id)} /></td>}
                  <td className="p-3 font-mono font-semibold">{v.equipment_code}</td>
                  <td className="p-3">
                    {v.category ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-50 text-[hsl(217,91%,24%)] border border-blue-200 text-xs font-medium">{v.category}</span>
                    ) : <span className="text-slate-400 text-xs">—</span>}
                  </td>
                  <td className="p-3">
                    <div className="font-medium">{v.description || [v.brand, v.model].filter(Boolean).join(' ') || '—'}</div>
                    <div className="text-xs text-slate-500">{[v.brand, v.model, v.year].filter(Boolean).join(' — ')}</div>
                  </td>
                  <td className="p-3 font-mono text-xs">{v.plate_no || <span className="text-slate-400">NA</span>}</td>
                  <td className="p-3 text-xs">{v.capacity || <span className="text-slate-400">—</span>}</td>
                  <td className="p-3">{v.company_assigned || '—'}</td>
                  <td className="p-3">{v.area || '—'}</td>
                  <td className="p-3">{v.project_location || '—'}</td>
                  <td className="p-3"><StatusBadge status={v.status} /></td>
                  <td className="p-3 text-xs">{formatDate(v.cr_expiration)}</td>
                  <td className="p-3 text-right">
                    {(() => {
                      const approvedCount = (v.photos || []).filter((p) => p.status === 'approved').length;
                      return approvedCount > 0 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setLightboxVehicle(v); setLightboxOpen(true); }}
                          title={`View ${approvedCount} photo${approvedCount > 1 ? 's' : ''}`}
                          data-testid={`equip-view-photos-${v.id}`}
                        >
                          <Camera className="h-4 w-4 text-[hsl(217,91%,24%)]" />
                          <span className="ml-1 text-[10px] font-semibold text-[hsl(217,91%,24%)]">{approvedCount}</span>
                        </Button>
                      );
                    })()}
                    <Button size="sm" variant="ghost" onClick={() => { setLpVehicle(v); setLpOpen(true); }} title="Manage Location & Photos" data-testid={`equip-lp-${v.id}`}>
                      <MapPin className={`h-4 w-4 ${v.location ? 'text-emerald-600' : 'text-slate-400'}`} />
                    </Button>
                    {canEdit && <Button size="sm" variant="ghost" onClick={() => openEdit(v)}><Edit2 className="h-4 w-4" /></Button>}
                    {canDelete && <Button size="sm" variant="ghost" onClick={() => remove(v)}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <PhotoLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        vehicle={lightboxVehicle}
        photos={lightboxVehicle?.photos || []}
      />

      <Dialog open={uploadOpen} onOpenChange={(o) => { if (!uploading) { setUploadOpen(o); if (!o) resetUpload(); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5" /> Upload Equipment Inventory</DialogTitle>
            <DialogDescription>
              Bulk import equipment from an Excel (.xlsx) or CSV file. The system uses the official template headers.
              Required column: <span className="font-semibold">Equipment Code</span>.
            </DialogDescription>
          </DialogHeader>

          {!uploadResult && (
            <div className="space-y-4">
              <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center bg-slate-50">
                <FileSpreadsheet className="h-10 w-10 text-slate-400 mx-auto" />
                <div className="mt-3 text-sm text-slate-700">
                  {uploadFile ? (
                    <span className="font-medium">{uploadFile.name} <span className="text-slate-500">({(uploadFile.size / 1024).toFixed(1)} KB)</span></span>
                  ) : (
                    <>Drop your file here or click <span className="font-semibold">Choose File</span></>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-1">Accepted: .xlsx, .csv &middot; Max 10MB</div>
                <div className="mt-4 flex items-center justify-center gap-2">
                  <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 bg-white text-sm font-medium hover:bg-slate-100 cursor-pointer">
                    <FileSpreadsheet className="h-4 w-4" /> Choose File
                    <input type="file" accept=".xlsx,.csv" className="hidden" onChange={onUploadFileChange} />
                  </label>
                  {uploadFile && (
                    <Button variant="ghost" size="sm" onClick={resetUpload}>Clear</Button>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-900">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold mb-0.5">Expected columns</div>
                  <div>Equipment Code, Equipment Category, Equipment Description, Type, Brand, Plate No, Model, Engine Number, Chassis Number, Serial Number, Status, Area, Location, Company Assigned, Latitude, Longitude, Date Acquired, CR Expiration, OR Expiration, TP Expiration, Last Maintenance Date, Next Maintenance Due, Capacity.</div>
                  <div className="mt-1">Duplicates by Equipment Code or Plate No (excluding blank/NA) are skipped.</div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Button variant="link" className="p-0 h-auto text-[hsl(217,91%,24%)]" onClick={downloadTemplate}>
                  <Download className="h-4 w-4 mr-1.5" /> Download template
                </Button>
              </div>
            </div>
          )}

          {uploadResult && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <ResultStat label="Added" value={uploadResult.created?.length || 0} icon={CheckCircle2} accent="emerald" />
                <ResultStat label="Skipped" value={uploadResult.skipped?.length || 0} icon={AlertCircle} accent="amber" />
                <ResultStat label="Errors" value={uploadResult.errors?.length || 0} icon={X} accent="rose" />
              </div>
              {(uploadResult.unknown_headers?.length || 0) > 0 && (
                <div className="text-xs bg-amber-50 border border-amber-200 rounded-md p-3 text-amber-900">
                  <span className="font-semibold">Unknown columns ignored:</span> {uploadResult.unknown_headers.join(', ')}
                </div>
              )}
              {(uploadResult.skipped?.length || 0) > 0 && (
                <div className="text-xs">
                  <div className="font-semibold text-amber-700 mb-1">Skipped rows</div>
                  <ScrollArea className="max-h-32 border rounded">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50"><tr><th className="p-2 text-left">Row</th><th className="p-2 text-left">Equipment Code</th><th className="p-2 text-left">Reason</th></tr></thead>
                      <tbody className="divide-y">
                        {uploadResult.skipped.map((s) => (
                          <tr key={`sk-${s.row}-${s.equipment_code || ''}`}><td className="p-2 font-mono">{s.row}</td><td className="p-2 font-mono">{s.equipment_code || '—'}</td><td className="p-2 text-slate-600">{s.reason}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                </div>
              )}
              {(uploadResult.errors?.length || 0) > 0 && (
                <div className="text-xs">
                  <div className="font-semibold text-red-700 mb-1">Errors</div>
                  <ScrollArea className="max-h-32 border rounded">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50"><tr><th className="p-2 text-left">Row</th><th className="p-2 text-left">Reason</th></tr></thead>
                      <tbody className="divide-y">
                        {uploadResult.errors.map((s) => (
                          <tr key={`err-${s.row}`}><td className="p-2 font-mono">{s.row}</td><td className="p-2 text-slate-600">{s.reason}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {!uploadResult ? (
              <>
                <Button variant="outline" onClick={() => { if (!uploading) { setUploadOpen(false); resetUpload(); } }} disabled={uploading}>Cancel</Button>
                <Button onClick={submitUpload} disabled={!uploadFile || uploading} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]">
                  {uploading ? 'Uploading…' : 'Upload'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={resetUpload}>Upload another</Button>
                <Button onClick={() => { setUploadOpen(false); resetUpload(); }} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]">Done</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Equipment' : 'Add Equipment'}</DialogTitle>
            <DialogDescription>Fill in the equipment details. Equipment code must be unique.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Equipment Code *" value={form.equipment_code} onChange={(v) => setForm({ ...form, equipment_code: v })} />
            <SelectField label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={dropdowns.equipment_category || []} />
            <Field label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} className="md:col-span-2" />
            <Field label="Make" value={form.make} onChange={(v) => setForm({ ...form, make: v })} />
            <Field label="Model" value={form.model} onChange={(v) => setForm({ ...form, model: v })} />
            <Field label="Year" type="number" value={form.year} onChange={(v) => setForm({ ...form, year: v })} />
            <Field label="Plate Number" value={form.plate_no} onChange={(v) => setForm({ ...form, plate_no: v })} placeholder="NA if unplated" />
            <Field label="Engine Number" value={form.engine_no} onChange={(v) => setForm({ ...form, engine_no: v })} />
            <Field label="Chassis Number" value={form.chassis_no} onChange={(v) => setForm({ ...form, chassis_no: v })} />
            <SelectField label="Company Assigned" value={form.company_assigned} onChange={(v) => setForm({ ...form, company_assigned: v })} options={dropdowns.company_assigned || []} />
            <SelectField label="Area" value={form.area} onChange={(v) => setForm({ ...form, area: v })} options={dropdowns.area || []} />
            <SelectField label="Location" value={form.project_location} onChange={(v) => setForm({ ...form, project_location: v })} options={dropdowns.project_location || []} />
            <SelectField label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] }))} />
            <Field label="Acquisition Date" type="date" value={form.acquisition_date} onChange={(v) => setForm({ ...form, acquisition_date: v })} />
            <Field label="CR Expiration" type="date" value={form.cr_expiration} onChange={(v) => setForm({ ...form, cr_expiration: v })} />
            <Field label="OR Expiration" type="date" value={form.or_expiration} onChange={(v) => setForm({ ...form, or_expiration: v })} />
            <Field label="TP Expiration" type="date" value={form.tp_expiration} onChange={(v) => setForm({ ...form, tp_expiration: v })} />
            <Field label="Capacity" value={form.capacity} onChange={(v) => setForm({ ...form, capacity: v })} placeholder="e.g. 20T, 5 cu.m, 1000 L" />
            <div className="md:col-span-2">
              <Label>Remarks</Label>
              <Textarea value={form.remarks || ''} onChange={(e) => setForm({ ...form, remarks: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EquipmentLocationPhotos
        open={lpOpen}
        onClose={() => { setLpOpen(false); setLpVehicle(null); }}
        vehicle={lpVehicle}
        onChanged={load}
      />
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', className = '', placeholder }) => (  <div className={className}>
    <Label>{label}</Label>
    <Input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
  </div>
);

const SelectField = ({ label, value, onChange, options }) => {
  const opts = options.map((o) => typeof o === 'string' ? { value: o, label: o } : o);
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger><SelectValue placeholder={`Select ${label.toLowerCase()}`} /></SelectTrigger>
        <SelectContent>
          {opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
};

const ResultStat = ({ label, value, icon: Icon, accent }) => {
  const colors = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
  };
  return (
    <div className={`border rounded-md p-3 flex items-center gap-3 ${colors[accent]}`}>
      <Icon className="h-5 w-5 shrink-0" />
      <div>
        <div className="text-2xl font-bold leading-none">{value}</div>
        <div className="text-xs mt-1">{label}</div>
      </div>
    </div>
  );
};

export default Equipment;
